import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://getfork.app";
const OG_IMAGE = `${SITE}/assets/fork-logo.png`;

interface SeoProps {
  title: string;
  description: string;
  /** Path only, e.g. "/privacy". Used for the canonical and og:url. */
  path: string;
  /** Keeps a page out of search results (used for 404). */
  noIndex?: boolean;
}

/**
 * Per-route metadata. Without this every route inherits index.html's tags —
 * including its canonical, which tells search engines the legal pages are
 * duplicates of the homepage.
 */
const Seo: React.FC<SeoProps> = ({ title, description, path, noIndex }) => {
  const url = `${SITE}${path}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {/* Always emit robots so this tag replaces index.html's rather than
          appending a second, conflicting one. */}
      <meta
        name="robots"
        content={
          noIndex
            ? "noindex, follow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={OG_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </Helmet>
  );
};

export default Seo;
